import React from 'react';
import * as Yup from 'yup';
import { Formik, FormikHelpers } from 'formik';

import { FunderReviewFormComponent } from './FunderReviewFormComponent';
import { ReviewerOption } from './types/ReviewerOption';
import { UserSchool } from './types/UserSchool';
import { FunderFormValues } from './types/FunderFormValues';
import { ReviewerType } from '../../../enums';
import { MESSAGE_MIN_LENGTH, MESSAGE_MAX_LENGTH } from './constants';

const validationSchema = (minCommentLength: number) => Yup.object().shape({
  stars: Yup.string()
    .required('Ce champ est obligatoire.'),
  offer: Yup.number().min(0).nullable(),
  estimate: Yup.number().min(0).nullable(),
  documents: Yup.number().min(0).nullable(),
  indulgence: Yup.number().min(0).nullable(),
  billing: Yup.number().min(0).nullable(),
  comment: Yup.string()
    .trim()
    .required('Ce champ est obligatoire.')
    .min(minCommentLength, `Veuillez fournir au moins ${minCommentLength} caractères.`)
    .max(MESSAGE_MAX_LENGTH, `Votre avis est trop long ! Nous limitons la longueur des avis à ${MESSAGE_MAX_LENGTH} caractères… essayez de le raccourcir un peu.`),
  lastName: Yup.string()
    .required('Ce champ est obligatoire.'),
  firstName: Yup.string()
    .required('Ce champ est obligatoire.'),
  email: Yup.string()
    .trim('Ce champ ne peut pas inclure d\'espace au début et à la fin')
    .strict(true)
    .email('Veuillez fournir une adresse électronique valide.')
    .required('Ce champ est obligatoire.'),
  conditionsAccepted: Yup.bool()
    .oneOf([true], 'Veuillez accepter les Conditions générales de publication des avis.'),
});

const referralValidationSchema = (minCommentLength: number) => Yup.object().shape({
  stars: Yup.string()
    .required('Ce champ est obligatoire.'),
  offer: Yup.number().min(0).nullable(),
  estimate: Yup.number().min(0).nullable(),
  documents: Yup.number().min(0).nullable(),
  indulgence: Yup.number().min(0).nullable(),
  billing: Yup.number().min(0).nullable(),
  comment: Yup.string()
    .trim()
    .required('Ce champ est obligatoire.')
    .min(minCommentLength, `Veuillez fournir au moins ${minCommentLength} caractères.`)
    .max(MESSAGE_MAX_LENGTH, `Votre avis est trop long ! Nous limitons la longueur des avis à ${MESSAGE_MAX_LENGTH} caractères… essayez de le raccourcir un peu.`),
  conditionsAccepted: Yup.bool()
    .oneOf([true], 'Veuillez accepter les Conditions générales de publication des avis.'),
});

const commonInitialValues = {
  stars: '',
  offer: null,
  estimate: null,
  documents: null,
  indulgence: null,
  billing: null,
  conditionsAccepted: false,
  comment: '',
};

const contactInitialValues = {
  firstName: '',
  lastName: '',
  email: '',
};

type FormProps = {
  setReviewerType?(type: ReviewerType): void;
  reviewersOptions?: ReviewerOption[];
  reviewTextRef: React.RefObject<HTMLTextAreaElement>;
  reviewerType: ReviewerType;
  onFormSubmit(values: FunderFormValues, {
    setErrors, setStatus, setSubmitting,
  }: FormikHelpers<FunderFormValues>): void;
  userSchools?: UserSchool[];
  isReferral?: boolean
  minCommentLength?: number;
};

const FunderReviewForm = ({
  reviewerType, onFormSubmit, setReviewerType, reviewersOptions, reviewTextRef, userSchools, isReferral, minCommentLength,
}: FormProps) => {
  let initialValues = {};
  if (isReferral) {
    initialValues = commonInitialValues;
  } else {
    initialValues = {
      ...commonInitialValues,
      ...contactInitialValues,
      reviewerType,
    };
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onFormSubmit}
      validationSchema={isReferral
        ? referralValidationSchema(minCommentLength || 200)
        : validationSchema(minCommentLength || 200)}
    >
      <FunderReviewFormComponent
        reviewerType={reviewerType}
        setReviewerType={setReviewerType}
        reviewersOptions={reviewersOptions}
        reviewTextRef={reviewTextRef}
        userSchools={userSchools}
        isReferral={isReferral}
        minCommentLength={minCommentLength}
      />
    </Formik>
  );
};

FunderReviewForm.defaultProps = {
  isReferral: false,
  setReviewerType: () => {},
  reviewersOptions: null,
  userSchools: [],
  minCommentLength: MESSAGE_MIN_LENGTH,
};

export { FunderReviewForm };
